// @flow

import React, { Component } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Row,
    Spinner,
    Table,
} from 'reactstrap';

export type MenuCatalogApi = {
    listLocations: () => Promise<Array<any>>,
    getCatalog: (locationId: string) => Promise<any>,
    saveDraft: (locationId: string, draft: any, sourceUrl?: string) => Promise<any>,
    importFromUrl: (locationId: string, url: string) => Promise<any>,
    publish: (locationId: string, force?: boolean) => Promise<any>,
    resync: (locationId: string, url?: string) => Promise<any>,
    seedDemo?: (locationId: string) => Promise<any>,
};

type PropsType = {
    api: MenuCatalogApi,
    title?: string,
    allowSeedDemo?: boolean,
    initialLocationId?: string,
};

type StateType = {
    loading: boolean,
    busy: boolean,
    error: ?string,
    notice: ?string,
    locations: Array<any>,
    locationId: string,
    sourceUrl: string,
    catalog: any,
    draftJson: string,
    editMode: 'form' | 'json',
};

function locationLabel(location: any): string {
    if (!location) return '';
    const name = location.name;
    if (Array.isArray(name) && name[0]) {
        return name[0].text || name[0].en || location.id;
    }
    if (typeof name === 'string') return name;
    return location.id;
}

function money(cents: number): string {
    return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
}

function itemName(item: any): string {
    if (!item) return '';
    if (item.name && typeof item.name === 'object') return item.name.en || item.name.fr || item.id;
    return item.name || item.id;
}

function gradeFromScore(score: number): string {
    if (score >= 70) return 'good';
    if (score >= 45) return 'review';
    return 'poor';
}

function flattenDraftItems(draft: any): Array<any> {
    const items = [];
    ((draft && draft.categories) || []).forEach((category) => {
        (category.items || []).forEach((item) => {
            if (!item || item.archived) return;
            items.push(item);
        });
    });
    return items;
}

/** Estimate how closely BillMaster's catalog matches the restaurant's public menu (0-100). */
function estimateMenuAccuracy(catalog: any, draft: any): ?{
    percent: number,
    grade: string,
    pricedCount: number,
    itemCount: number,
    imagedCount: number,
    fromImport: boolean,
    label: string,
} {
    const quality = catalog && catalog.import_meta && catalog.import_meta.quality;
    if (quality && Number.isFinite(Number(quality.score))) {
        const percent = Math.round(Number(quality.score));
        return {
            percent,
            grade: quality.grade || gradeFromScore(percent),
            pricedCount: Number(quality.priced_count) || 0,
            itemCount: Number(quality.item_count) || 0,
            imagedCount: Number(quality.imaged_count) || 0,
            fromImport: true,
            label: 'vs their public menu (last import)',
        };
    }

    const items = flattenDraftItems(draft);
    const itemCount = items.length;
    if (!itemCount) return null;

    const pricedCount = items.filter((item) => (item.price_cents || 0) > 0).length;
    const imagedCount = items.filter((item) => item.image).length;
    const pricedRatio = pricedCount / itemCount;
    let score = 0;
    if (itemCount >= 20) score += 30;
    else if (itemCount >= 8) score += 25;
    else if (itemCount >= 3) score += 12;
    score += Math.min(40, Math.round(pricedRatio * 40));
    if (imagedCount) score += Math.min(20, imagedCount * 2);

    const sourceUrl = (catalog && (catalog.source_url || (catalog.import_meta && catalog.import_meta.source_url))) || '';
    if (String(sourceUrl).startsWith('seed://') && pricedRatio >= 0.9) {
        score = Math.max(score, 98);
    }

    score = Math.max(0, Math.min(100, score));
    return {
        percent: score,
        grade: gradeFromScore(score),
        pricedCount,
        itemCount,
        imagedCount,
        fromImport: false,
        label: String(sourceUrl).startsWith('seed://')
            ? 'vs seeded restaurant menu'
            : 'estimated from current draft coverage',
    };
}

export default class MenuCatalogManager extends Component<PropsType, StateType> {
    constructor(props: PropsType) {
        super(props);
        this.state = {
            loading: true,
            busy: false,
            error: null,
            notice: null,
            locations: [],
            locationId: props.initialLocationId || '',
            sourceUrl: '',
            catalog: null,
            draftJson: '',
            editMode: 'form',
        };
    }

    componentDidMount() {
        this.bootstrap();
    }

    bootstrap = async () => {
        this.setState({ loading: true, error: null });
        try {
            const locations = await this.props.api.listLocations();
            const list = Array.isArray(locations) ? locations : [];
            const locationId = this.state.locationId || (list[0] && list[0].id) || '';
            this.setState({ locations: list, locationId, loading: false }, () => {
                if (locationId) this.loadCatalog(locationId);
            });
        } catch (error) {
            this.setState({ loading: false, error: error.message || String(error) });
        }
    };

    loadCatalog = async (locationId: string) => {
        this.setState({ busy: true, error: null, notice: null });
        try {
            const catalog = await this.props.api.getCatalog(locationId);
            const draft = catalog.draft || catalog.published || { categories: [], currency: 'CAD', name: 'Menu' };
            this.setState({
                busy: false,
                catalog,
                sourceUrl: catalog.source_url || '',
                draftJson: JSON.stringify(draft, null, 2),
            });
        } catch (error) {
            this.setState({ busy: false, error: error.message || String(error) });
        }
    };

    onSelectLocation = (event: any) => {
        const locationId = event.target.value;
        this.setState({ locationId }, () => this.loadCatalog(locationId));
    };

    parseDraft = () => {
        try {
            return JSON.parse(this.state.draftJson);
        } catch (error) {
            throw new Error('Draft JSON is invalid');
        }
    };

    run = async (action: () => Promise<any>, successNotice: string) => {
        this.setState({ busy: true, error: null, notice: null });
        try {
            const catalog = await action();
            const draft = catalog.draft || catalog.published || { categories: [] };
            this.setState({
                busy: false,
                catalog,
                sourceUrl: catalog.source_url || this.state.sourceUrl,
                draftJson: JSON.stringify(draft, null, 2),
                notice: successNotice,
            });
        } catch (error) {
            this.setState({ busy: false, error: error.message || String(error) });
        }
    };

    handleImport = () => {
        const { locationId, sourceUrl } = this.state;
        if (!locationId || !sourceUrl.trim()) {
            this.setState({ error: 'Select a location and paste a public menu URL' });
            return;
        }
        this.run(() => this.props.api.importFromUrl(locationId, sourceUrl.trim()), 'Import finished - review draft, then publish');
    };

    handleResync = () => {
        const { locationId, sourceUrl } = this.state;
        if (!locationId) return;
        this.run(() => this.props.api.resync(locationId, sourceUrl.trim() || undefined), 'Re-sync draft ready - review diff, then publish');
    };

    handleSaveDraft = () => {
        const { locationId, sourceUrl } = this.state;
        try {
            const draft = this.parseDraft();
            this.run(() => this.props.api.saveDraft(locationId, draft, sourceUrl.trim() || undefined), 'Draft saved');
        } catch (error) {
            this.setState({ error: error.message || String(error) });
        }
    };

    handlePublish = (force: boolean = false) => {
        const { locationId, catalog } = this.state;
        const quality = catalog && catalog.import_meta && catalog.import_meta.quality;
        if (!force && quality && quality.publish_recommended === false) {
            this.setState({
                error:
                    'Publish blocked: add prices (or improve import quality) first. Use Force publish only if you intend to override.',
            });
            return;
        }
        this.run(
            () => this.props.api.publish(locationId, force),
            force
                ? 'Force-published - guests and POS will use this catalog'
                : 'Published - guests and POS will use this catalog',
        );
    };

    handleSeedDemo = () => {
        if (!this.props.api.seedDemo || !this.state.locationId) return;
        this.run(() => this.props.api.seedDemo(this.state.locationId), 'Demo catalog seeded and published');
    };

    updateItemField = (categoryIndex: number, itemIndex: number, field: string, value: any) => {
        try {
            const draft = this.parseDraft();
            const item = draft.categories[categoryIndex].items[itemIndex];
            if (field === 'name') {
                item.name = { en: value, fr: (item.name && item.name.fr) || value };
            } else if (field === 'description') {
                item.description = { en: value, fr: (item.description && item.description.fr) || value };
            } else if (field === 'price') {
                item.price_cents = Math.round(parseFloat(value || '0') * 100) || 0;
            } else if (field === 'tags') {
                item.tags = String(value || '')
                    .split(',')
                    .map((t) => t.trim())
                    .filter(Boolean);
            }
            this.setState({ draftJson: JSON.stringify(draft, null, 2) });
        } catch (error) {
            this.setState({ error: error.message || String(error) });
        }
    };

    renderDiff() {
        const diff = this.state.catalog && this.state.catalog.last_diff;
        if (!diff) return null;
        const added = (diff.added || []).length;
        const changed = (diff.changed || []).length;
        const removed = (diff.removed || []).length;
        if (!added && !changed && !removed && diff.note !== 'published') return null;
        return (
            <Alert color="info" className="mb-3">
                <strong>Sync diff vs published:</strong>{' '}
                {added} added / {changed} changed / {removed} removed
                {diff.note ? ` (${diff.note})` : ''}
            </Alert>
        );
    }

    renderWarnings() {
        const warnings = (this.state.catalog && this.state.catalog.import_meta && this.state.catalog.import_meta.warnings) || [];
        if (!warnings.length) return null;
        return (
            <Alert color="warning" className="mb-3">
                <strong>Import warnings</strong>
                <ul className="mb-0 mt-2">
                    {warnings.slice(0, 12).map((w) => (
                        <li key={w}>{w}</li>
                    ))}
                </ul>
            </Alert>
        );
    }

    renderAccuracy(accuracy: any) {
        if (!accuracy) return null;
        const color = accuracy.grade === 'good' ? 'success' : accuracy.grade === 'review' ? 'warning' : 'danger';
        const reassurance = accuracy.percent >= 85
            ? 'Strong match - safe to review and publish with confidence.'
            : accuracy.percent >= 60
                ? 'Solid coverage - spot-check a few prices and names before publishing.'
                : 'Low match - re-import or fix missing prices/names before publishing.';
        const quality = this.state.catalog && this.state.catalog.import_meta && this.state.catalog.import_meta.quality;
        return (
            <Alert color={color} className="mb-3" data-testid="menu-accuracy-panel">
                <div className="d-flex flex-wrap align-items-center justify-content-between" style={{ gap: 12 }}>
                    <div>
                        <div className="text-uppercase small font-weight-bold" style={{ letterSpacing: '0.08em' }}>
                            Menu accuracy
                        </div>
                        <div style={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.1 }}>
                            {accuracy.percent}%
                        </div>
                        <div className="small mt-1">{accuracy.label}</div>
                    </div>
                    <div className="flex-grow-1" style={{ minWidth: 220 }}>
                        <strong>{reassurance}</strong>
                        <div className="mt-1">
                            {accuracy.pricedCount}/{accuracy.itemCount} items priced
                            {accuracy.imagedCount ? ` / ${accuracy.imagedCount} with images` : ''}
                        </div>
                        {quality && quality.publish_recommended === false ? (
                            <div className="mt-1">Not recommended to publish yet - improve coverage first.</div>
                        ) : null}
                        {quality && quality.extractor ? (
                            <div className="small text-muted mt-1">Source extractor: {quality.extractor}</div>
                        ) : null}
                    </div>
                </div>
            </Alert>
        );
    }

    renderFormEditor() {
        let draft;
        try {
            draft = this.parseDraft();
        } catch (error) {
            return <Alert color="danger">Fix JSON before using form editor: {error.message}</Alert>;
        }
        const categories = draft.categories || [];
        if (!categories.length) {
            return <p className="text-muted mb-0">No categories yet. Import a URL or edit JSON.</p>;
        }
        return categories.map((category, categoryIndex) => (
            <Card key={category.id || categoryIndex} className="mb-3" style={{ border: '1px solid #dbe4ea' }}>
                <CardHeader className="bg-white">
                    <strong>{(category.name && (category.name.en || category.name.fr)) || category.id}</strong>
                    <Badge color="light" className="ml-2">{(category.items || []).length} items</Badge>
                </CardHeader>
                <CardBody className="p-0">
                    <Table responsive size="sm" className="mb-0">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th style={{ width: 100 }}>Price</th>
                                <th>Description</th>
                                <th>Tags</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(category.items || []).map((item, itemIndex) => (
                                <tr key={item.id || itemIndex} style={item.archived ? { opacity: 0.5 } : undefined}>
                                    <td>
                                        <Input
                                            bsSize="sm"
                                            value={itemName(item)}
                                            onChange={(e) => this.updateItemField(categoryIndex, itemIndex, 'name', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <Input
                                            bsSize="sm"
                                            type="number"
                                            step="0.01"
                                            value={((item.price_cents || 0) / 100).toFixed(2)}
                                            onChange={(e) => this.updateItemField(categoryIndex, itemIndex, 'price', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <Input
                                            bsSize="sm"
                                            value={(item.description && item.description.en) || ''}
                                            onChange={(e) => this.updateItemField(categoryIndex, itemIndex, 'description', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <Input
                                            bsSize="sm"
                                            value={(item.tags || []).join(', ')}
                                            onChange={(e) => this.updateItemField(categoryIndex, itemIndex, 'tags', e.target.value)}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </CardBody>
            </Card>
        ));
    }

    render() {
        const { title = 'Menu catalog', allowSeedDemo } = this.props;
        const { loading, busy, error, notice, locations, locationId, sourceUrl, catalog, draftJson, editMode } = this.state;

        if (loading) {
            return (
                <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-3 text-muted">Loading menu tools...</p>
                </div>
            );
        }

        const status = (catalog && catalog.status) || 'empty';
        const itemCount = catalog && catalog.import_meta && catalog.import_meta.item_count;
        let draftForAccuracy = null;
        try {
            draftForAccuracy = this.parseDraft();
        } catch (error) {
            draftForAccuracy = (catalog && (catalog.draft || catalog.published)) || null;
        }
        const accuracy = estimateMenuAccuracy(catalog, draftForAccuracy);
        const accuracyBadgeColor = accuracy
            ? (accuracy.grade === 'good' ? 'success' : accuracy.grade === 'review' ? 'warning' : 'danger')
            : 'secondary';

        return (
            <div data-testid="menu-catalog-manager">
                <div className="d-flex flex-wrap justify-content-between align-items-start mb-3">
                    <div>
                        <h2 className="h4 mb-1">{title}</h2>
                        <p className="text-muted mb-0">
                            Pull the restaurant&apos;s public online menu, review the draft, then publish for guests and staff.
                        </p>
                    </div>
                    <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                        <Badge color={status === 'published' ? 'success' : status === 'draft' ? 'warning' : 'secondary'} pill className="px-3 py-2">
                            {status}
                        </Badge>
                        {itemCount != null ? <Badge color="light" pill className="px-3 py-2">{itemCount} items</Badge> : null}
                        {accuracy ? (
                            <Badge color={accuracyBadgeColor} pill className="px-3 py-2" data-testid="menu-accuracy-badge">
                                {accuracy.percent}% match
                            </Badge>
                        ) : null}
                    </div>
                </div>

                {error ? <Alert color="danger">{error}</Alert> : null}
                {notice ? <Alert color="success">{notice}</Alert> : null}
                {this.renderDiff()}
                {this.renderAccuracy(accuracy)}
                {this.renderWarnings()}

                <Card className="mb-3" style={{ border: '1px solid #dbe4ea' }}>
                    <CardBody>
                        <Form>
                            <Row>
                                <Col md="4">
                                    <FormGroup>
                                        <Label>Location</Label>
                                        <Input type="select" value={locationId} onChange={this.onSelectLocation} disabled={busy}>
                                            {!locations.length ? <option value="">No locations</option> : null}
                                            {locations.map((loc) => (
                                                <option key={loc.id} value={loc.id}>
                                                    {locationLabel(loc)}
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md="8">
                                    <FormGroup>
                                        <Label>Public menu URL</Label>
                                        <Input
                                            type="url"
                                            placeholder="https://restaurant.com/menu"
                                            value={sourceUrl}
                                            onChange={(e) => this.setState({ sourceUrl: e.target.value })}
                                            disabled={busy}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                                <Button color="primary" onClick={this.handleImport} disabled={busy || !locationId}>
                                    {busy ? 'Working...' : 'Import from URL'}
                                </Button>
                                <Button color="secondary" outline onClick={this.handleResync} disabled={busy || !locationId}>
                                    Re-sync
                                </Button>
                                <Button color="secondary" onClick={this.handleSaveDraft} disabled={busy || !locationId}>
                                    Save draft
                                </Button>
                                <Button
                                    color="success"
                                    onClick={() => this.handlePublish(false)}
                                    disabled={
                                        busy ||
                                        !locationId ||
                                        (catalog &&
                                            catalog.import_meta &&
                                            catalog.import_meta.quality &&
                                            catalog.import_meta.quality.publish_recommended === false)
                                    }
                                >
                                    Publish
                                </Button>
                                {catalog &&
                                catalog.import_meta &&
                                catalog.import_meta.quality &&
                                catalog.import_meta.quality.publish_recommended === false ? (
                                    <Button
                                        color="warning"
                                        outline
                                        onClick={() => this.handlePublish(true)}
                                        disabled={busy || !locationId}
                                    >
                                        Force publish
                                    </Button>
                                ) : null}
                                {allowSeedDemo && this.props.api.seedDemo ? (
                                    <Button color="link" onClick={this.handleSeedDemo} disabled={busy || !locationId}>
                                        Seed Downtown Cafe demo
                                    </Button>
                                ) : null}
                            </div>
                        </Form>
                    </CardBody>
                </Card>

                <div className="mb-3">
                    <Button
                        size="sm"
                        color={editMode === 'form' ? 'dark' : 'light'}
                        className="mr-2"
                        onClick={() => this.setState({ editMode: 'form' })}
                    >
                        Form editor
                    </Button>
                    <Button
                        size="sm"
                        color={editMode === 'json' ? 'dark' : 'light'}
                        onClick={() => this.setState({ editMode: 'json' })}
                    >
                        JSON
                    </Button>
                </div>

                {editMode === 'json' ? (
                    <FormGroup>
                        <Label>Draft catalog JSON</Label>
                        <Input
                            type="textarea"
                            rows="18"
                            value={draftJson}
                            onChange={(e) => this.setState({ draftJson: e.target.value })}
                            style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}
                        />
                    </FormGroup>
                ) : (
                    this.renderFormEditor()
                )}

                {catalog && catalog.published ? (
                    <Card className="mt-3" style={{ border: '1px solid #dbe4ea' }}>
                        <CardHeader className="bg-white">Published snapshot</CardHeader>
                        <CardBody>
                            <p className="mb-2 text-muted">
                                Guests see the published catalog. Sample prices:{' '}
                                {((catalog.published.categories || [])[0]?.items || [])
                                    .slice(0, 3)
                                    .map((item) => `${itemName(item)} (${money(item.price_cents)})`)
                                    .join(' / ') || 'none'}
                            </p>
                        </CardBody>
                    </Card>
                ) : null}
            </div>
        );
    }
}
